import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useSettingsStore } from '@hooks/useStores';
import { useToastContext } from '@contexts/ToastContext';
import { ArrowLeftIcon, CheckIcon } from '@components/common/ui/icons';
import type { Accent, Theme, Keybindings, Language } from '@stores/settingsStore';
import { DEFAULT_KEYBINDINGS } from '@stores/settingsStore';
import * as styles from '@pages/ProfilePage.module.css';
import $api from '@http';

type TabKey = 'profile' | 'security' | 'preferences';

const normalizeRole = (raw: unknown): string => {
    if (!raw) return 'User';
    const v = String(raw).toLowerCase();
    if (v === 'user') return 'User';
    if (v === 'admin' || v === 'administrator') return 'Admin';
    if (v === 'moderator' || v === 'mod') return 'Moderator';
    if (/^[0-9a-f]{24}$/i.test(v)) return 'User';
    return v.charAt(0).toUpperCase() + v.slice(1);
};

const ACCENTS: { key: Accent; color: string; label: string }[] = [
    { key: 'amber', color: '#d97706', label: 'Amber' },
    { key: 'indigo', color: '#6366f1', label: 'Indigo' },
    { key: 'slate', color: '#4a7fa5', label: 'Slate' },
    { key: 'forest', color: '#5a8a5a', label: 'Forest' },
    { key: 'rose', color: '#d4677a', label: 'Rose' },
];

// ── Field helper ──────────────────────────────────────────────────────────────
const Field: React.FC<{ label: string; helpText?: string; children: React.ReactNode }> = ({
    label,
    helpText,
    children,
}) => (
    <div className={styles.field}>
        <label className={styles.fieldLabel}>{label}</label>
        {children}
        {helpText && <p className={styles.helpText}>{helpText}</p>}
    </div>
);

// ── PrefRow helper ────────────────────────────────────────────────────────────
const PrefRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className={styles.prefRow}>
        <span className={styles.prefLabel}>{label}</span>
        {children}
    </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
export const ProfilePage: React.FC = observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authStore = useAuthStore();
    const settingsStore = useSettingsStore();
    const toast = useToastContext();
    const [searchParams] = useSearchParams();

    const initialTab = (['profile', 'security', 'preferences'] as TabKey[]).includes(
        searchParams.get('tab') as TabKey,
    )
        ? (searchParams.get('tab') as TabKey)
        : 'profile';

    const [tab, setTab] = useState<TabKey>(initialTab);
    const [saved, setSaved] = useState(false);
    const [capturingKey, setCapturingKey] = useState<keyof Keybindings | null>(null);

    const baseUser: any = authStore.user || {};
    const [profile, setProfile] = useState({
        name: baseUser.name || baseUser.username || 'User',
        login: baseUser.login || baseUser.username || '',
        email: baseUser.email || '',
        bio: baseUser.about || '',
        role: normalizeRole(baseUser.role),
    });

    const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });

    const [stats, setStats] = useState({ totalNotes: 0, sharedNotes: 0 });

    useEffect(() => {
        const u: any = authStore.user;
        if (!u) return;
        setProfile((prev) => ({
            ...prev,
            name: u.name || u.username || prev.name,
            login: u.login || u.username || prev.login,
            email: u.email || prev.email,
            bio: u.about ?? prev.bio,
            role: normalizeRole(u.role ?? prev.role),
        }));
    }, [authStore.user]);

    useEffect(() => {
        $api.get('/notes')
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                const shared = data.filter((n: any) => n.isPublic || n.access?.length > 0).length;
                setStats({ totalNotes: data.length, sharedNotes: shared });
            })
            .catch((err) => {
                console.warn('Failed to load note stats:', err);
            });
    }, []);
    const initials =
        profile.name
            .split(' ')
            .map((n: string) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U';

    const handleSaveProfile = async () => {
        try {
            const res = await $api.patch('/users/me', {
                name: profile.name,
                login: profile.login,
                about: profile.bio,
            });
            authStore.setUser(res.data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            toast.success(t('profile.actions.profileUpdated'));
        } catch (e: any) {
            toast.error(e?.response?.data?.message || t('profile.actions.profileUpdateFailed'));
        }
    };
    const handleChangePassword = async () => {
        if (!passwords.current || !passwords.next || !passwords.confirm) {
            toast.warning(t('profile.actions.passwordFillAll'));
            return;
        }
        if (passwords.next !== passwords.confirm) {
            toast.warning(t('profile.actions.passwordMismatch'));
            return;
        }
        try {
            await authStore.changePassword(passwords.current, passwords.next);
            toast.success(t('profile.actions.passwordUpdated'));
            setPasswords({ current: '', next: '', confirm: '' });
        } catch (e: any) {
            toast.error(e?.response?.data?.message || t('profile.actions.passwordUpdateFailed'));
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={styles.page}>
            {/* Topbar */}
            <header className={styles.topbar}>
                <button className={styles.topbarBack} onClick={() => navigate('/')}>
                    <ArrowLeftIcon className={styles.topbarBackIcon} />
                    {t('profile.title')}
                </button>
            </header>

            <div className={styles.content}>
                {/* User header */}
                <div className={styles.userHeader}>
                    <div className={styles.avatar}>{initials}</div>
                    <div className={styles.userInfo}>
                        <div className={styles.userNameRow}>
                            <h1 className={styles.userName}>{profile.name}</h1>
                            {authStore.user?.isActivated ? (
                                <span className={styles.badgeActive}>{t('profile.activation.active')}</span>
                            ) : (
                                <span className={styles.badgeInactive}>{t('profile.activation.inactive')}</span>
                            )}
                        </div>
                        <p className={styles.userMeta}>
                            @{profile.login || profile.email} &middot;{' '}
                            {t('profile.stats', { total: stats.totalNotes, shared: stats.sharedNotes })}
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <div className={styles.tabs}>
                    {(['profile', 'security', 'preferences'] as TabKey[]).map((tabKey) => (
                        <button
                            key={tabKey}
                            className={`${styles.tab} ${tab === tabKey ? styles.tabActive : ''}`}
                            onClick={() => setTab(tabKey)}
                        >
                            {t(`profile.tab.${tabKey}`)}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className={styles.tabContent}>
                    {tab === 'profile' && (
                        <div className={styles.fields}>
                            <Field label={t('profile.fields.fullName')}>
                                <input
                                    className={styles.input}
                                    value={profile.name}
                                    onChange={(e) =>
                                        setProfile({ ...profile, name: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label={t('profile.fields.login')}>
                                <input
                                    className={styles.input}
                                    value={profile.login}
                                    onChange={(e) =>
                                        setProfile({ ...profile, login: e.target.value })
                                    }
                                    placeholder={t('profile.fields.loginPlaceholder')}
                                />
                            </Field>
                            <Field label={t('profile.fields.email')} helpText={t('profile.fields.emailHint')}>
                                <input
                                    className={styles.input}
                                    type="email"
                                    value={profile.email}
                                    disabled
                                />
                            </Field>
                            <Field label={t('profile.fields.bio')}>
                                <textarea
                                    className={styles.textarea}
                                    rows={3}
                                    value={profile.bio}
                                    onChange={(e) =>
                                        setProfile({ ...profile, bio: e.target.value })
                                    }
                                    placeholder={t('profile.fields.bioPlaceholder')}
                                />
                            </Field>
                        </div>
                    )}

                    {tab === 'security' && (
                        <div className={styles.fields}>
                            <div className={styles.card}>
                                <h3 className={styles.cardTitle}>{t('profile.security.changePassword')}</h3>
                                <div className={styles.cardBody}>
                                    <input
                                        className={styles.input}
                                        type="password"
                                        placeholder={t('profile.security.currentPassword')}
                                        value={passwords.current}
                                        onChange={(e) =>
                                            setPasswords({ ...passwords, current: e.target.value })
                                        }
                                    />
                                    <input
                                        className={styles.input}
                                        type="password"
                                        placeholder={t('profile.security.newPassword')}
                                        value={passwords.next}
                                        onChange={(e) =>
                                            setPasswords({ ...passwords, next: e.target.value })
                                        }
                                    />
                                    <input
                                        className={styles.input}
                                        type="password"
                                        placeholder={t('profile.security.confirmPassword')}
                                        value={passwords.confirm}
                                        onChange={(e) =>
                                            setPasswords({ ...passwords, confirm: e.target.value })
                                        }
                                    />
                                    <button
                                        className={styles.btnPrimary}
                                        onClick={handleChangePassword}
                                    >
                                        {t('profile.security.updatePassword')}
                                    </button>
                                </div>
                            </div>
                            <div className={styles.card}>
                                <div className={styles.cardRow}>
                                    <div>
                                        <h3 className={styles.cardTitle}>{t('profile.security.twoFactor')}</h3>
                                        <p className={styles.helpText}>{t('profile.security.comingSoon')}</p>
                                    </div>
                                    <span className={styles.badgeSoon}>{t('profile.security.soon')}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === 'preferences' && (
                        <div className={styles.prefList}>
                            <PrefRow label={t('profile.preferences.theme')}>
                                <div className={styles.btnGroup}>
                                    {(['light', 'dark'] as Theme[]).map((themeValue) => (
                                        <button
                                            key={themeValue}
                                            className={`${styles.groupBtn} ${settingsStore.theme === themeValue ? styles.groupBtnActive : ''}`}
                                            onClick={() => settingsStore.setTheme(themeValue)}
                                        >
                                            {themeValue === 'light' ? t('common.light') : t('common.dark')}
                                        </button>
                                    ))}
                                </div>
                            </PrefRow>

                            <PrefRow label={t('profile.preferences.accent')}>
                                <div className={styles.accentDots}>
                                    {ACCENTS.map((a) => (
                                        <button
                                            key={a.key}
                                            className={styles.accentDot}
                                            title={a.label}
                                            style={{
                                                background: a.color,
                                                outline:
                                                    settingsStore.accent === a.key
                                                        ? `2px solid ${a.color}`
                                                        : 'none',
                                                outlineOffset: 2,
                                            }}
                                            onClick={() => settingsStore.setAccent(a.key)}
                                        >
                                            {settingsStore.accent === a.key && (
                                                <CheckIcon className={styles.accentCheck} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </PrefRow>

                            <PrefRow label={t('profile.preferences.language')}>
                                <div className={styles.btnGroup}>
                                    {(['en', 'ru'] as Language[]).map((lang) => (
                                        <button
                                            key={lang}
                                            className={`${styles.groupBtn} ${settingsStore.language === lang ? styles.groupBtnActive : ''}`}
                                            onClick={() => settingsStore.setLanguage(lang)}
                                        >
                                            {lang.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            </PrefRow>

                            <div className={styles.keybindingsSection}>
                                <div className={styles.keybindingsSectionHeader}>
                                    <span>{t('profile.preferences.editorShortcuts')}</span>
                                    <button
                                        className={styles.resetBtn}
                                        onClick={() => { settingsStore.resetKeybindings(); setCapturingKey(null); }}
                                    >
                                        {t('profile.preferences.resetShortcuts')}
                                    </button>
                                </div>
                                {(Object.keys(DEFAULT_KEYBINDINGS) as (keyof Keybindings)[]).map((action) => (
                                    <div key={action} className={styles.keybindingRow}>
                                        <span className={styles.keybindingLabel}>
                                            {action === 'duplicateLine'     ? t('profile.shortcuts.duplicateLine') :
                                             action === 'deleteLine'        ? t('profile.shortcuts.deleteLine') :
                                             action === 'moveParagraphUp'   ? t('profile.shortcuts.moveParagraphUp') :
                                             action === 'moveParagraphDown' ? t('profile.shortcuts.moveParagraphDown') :
                                             action === 'insertHR'          ? t('profile.shortcuts.insertHR') :
                                             action}
                                        </span>
                                        <button
                                            className={`${styles.keybindingInput}${capturingKey === action ? ` ${styles.keybindingCapturing}` : ''}`}
                                            onClick={() => setCapturingKey(action)}
                                            onKeyDown={(e) => {
                                                if (capturingKey !== action) return;
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e.key === 'Escape') { setCapturingKey(null); return; }
                                                const parts: string[] = [];
                                                if (e.metaKey || e.ctrlKey) parts.push('Mod');
                                                if (e.altKey) parts.push('Alt');
                                                if (e.shiftKey) parts.push('Shift');
                                                const key = e.key.length === 1 ? e.key.toLowerCase()
                                                    : e.key === 'ArrowUp' ? 'Up'
                                                    : e.key === 'ArrowDown' ? 'Down'
                                                    : e.key === 'ArrowLeft' ? 'Left'
                                                    : e.key === 'ArrowRight' ? 'Right'
                                                    : e.key;
                                                if (['Control','Meta','Alt','Shift'].includes(key)) return;
                                                parts.push(key);
                                                settingsStore.setKeybinding(action, parts.join('-'));
                                                setCapturingKey(null);
                                            }}
                                            onBlur={() => setCapturingKey(null)}
                                        >
                                            {capturingKey === action
                                                ? t('profile.preferences.pressKeyCombo')
                                                : settingsStore.keybindings[action].replace(/\bMod\b/g, 'Ctrl')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <button className={styles.btnSecondary} onClick={() => navigate('/')}>
                        {t('common.cancel')}
                    </button>
                    {tab === 'profile' && (
                        <button className={styles.btnPrimary} onClick={handleSaveProfile}>
                            {saved && <CheckIcon className={styles.saveIcon} />}
                            {saved ? t('common.saved') : t('common.saveChanges')}
                        </button>
                    )}{' '}
                </div>
            </div>
        </div>
    );
});
